import { EMERGENCY_DIAL, EMERGENCY_NOTE } from '@adhd/shared';
import type { CbtTool, CrisisResource } from '@adhd/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { api, ApiError, type SosStart } from '../lib/api.js';

/**
 * SOS.
 *
 * Rules this screen follows, all of them learned from how people actually
 * behave at peak distress:
 *
 * - No form stands between arriving and getting help. The event is logged on
 *   mount; the "how bad is it" question comes AFTER, and is skippable.
 * - Tools are ordered by what has measurably worked for THIS person before.
 * - Steps are shown one at a time. A wall of six bullets does not get read.
 * - Crisis contacts are reachable from every state, never buried.
 * - Closing out is optional. Someone who feels better and walks away has used
 *   this correctly; nagging them for a rating would be the app serving itself.
 */

type Stage = 'choose' | 'doing' | 'closing' | 'done';

export function Sos(): JSX.Element {
  const navigate = useNavigate();
  const [session, setSession] = useState<SosStart | null>(null);
  const [stage, setStage] = useState<Stage>('choose');
  const [tool, setTool] = useState<CbtTool | null>(null);
  const [step, setStep] = useState(0);
  const [after, setAfter] = useState(50);
  /*
   * How bad it was when they opened this, asked HERE and not on arrival.
   *
   * `distress_before` has been null on every event since the feature shipped,
   * so the recipient page draws a pair that can never be filled. The fix is
   * not to ask on the way in: somebody opening this screen is already
   * activated, and a slider between them and the thing that helps costs more
   * than the number is worth.
   *
   * Null until touched, like the mood control. An untouched slider that posts
   * 50 would fill fourteen null columns with a default nobody chose, which is
   * a worse kind of missing than missing.
   */
  const [recalled, setRecalled] = useState<number | null>(null);
  const [showCrisis, setShowCrisis] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const started = useRef(false);
  // Set once the event exists, so a second action does not create a second one.
  const sosIdRef = useRef<string | null>(null);
  // The creation while it is still in the air, so a second caller joins it
  // rather than starting a second moment.
  const pendingRef = useRef<Promise<string | null> | null>(null);
  /*
   * HOW BAD, ON THE WAY IN — release 3 of 2.0 (docs/PRD-2.0.md §8). Eleven
   * targets, 0 to 10, skippable, below the emergency block and above the
   * tools; stored ×10 as `distressBefore`, the column that has been null on
   * every visit since the feature shipped (14 of 14 on 9 Oct). Answering it
   * starts the visit at once so the answer cannot be lost to a tool never
   * being opened. Static, offline, no model: the crisis path still depends
   * on nothing.
   */
  const [before, setBefore] = useState<number | null>(null);
  const beforeRef = useRef<number | null>(null);

  /*
   * Load the screen without recording that it was opened.
   *
   * This used to create the event on arrival — no gate, no question — and the
   * instinct behind that is still right: nothing here asks anything before
   * helping. But it meant tapping Help now to see what it does, or landing on it
   * by accident, wrote a crisis moment into the record. Those are counted on the
   * trends screen, in the weekly report, and now in the line a partner reads
   * about the day, so a curious tap became "he asked for help three times
   * today".
   *
   * The event is created by the first thing actually done here instead. The
   * screen is no slower and nothing is gated; only what a visit means changes.
   */
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api
      .sosTools()
      .then(setSession)
      .catch((err: unknown) =>
        setFailure(err instanceof ApiError ? err : new ApiError(0, 'unknown', 'Something went wrong.')),
      );
  }, []);

  /**
   * The event, created on demand and only once.
   *
   * Returns null if it cannot be created — and every caller must carry on
   * anyway. Failing to record a visit is a lost row; refusing to show someone a
   * breathing exercise because the write failed is the app choosing its own
   * bookkeeping over the person using it.
   *
   * The in-flight promise is held, not just the finished id. Without it, two
   * calls close together — a one-step tool where "Done — finish" and the button
   * on the next screen are half a second apart — both saw an empty ref and
   * posted, and one visit became two moments in the record.
   */
  const ensureEvent = useCallback((): Promise<string | null> => {
    if (sosIdRef.current) return Promise.resolve(sosIdRef.current);
    if (pendingRef.current) return pendingRef.current;
    pendingRef.current = api
      .startSos(beforeRef.current === null ? {} : { distressBefore: beforeRef.current * 10 })
      .then((created) => {
        sosIdRef.current = created.sosId;
        return created.sosId;
      })
      .catch(() => null)
      .finally(() => {
        pendingRef.current = null;
      });
    return pendingRef.current;
  }, []);

  /**
   * The event if one was started, waiting on a creation still in the air.
   *
   * Never starts one, and that distinction is the whole of what "counts". The
   * closing screen is reached two ways: after working through a tool, and
   * straight from "I'm okay now" without touching anything. Creating the event
   * from there meant tapping Help now and immediately saying you were fine
   * wrote a crisis moment into the record — the same failure as recording the
   * visit on arrival, moved one screen along and reached by the button that
   * says nothing is wrong.
   */
  const startedEvent = useCallback(
    (): Promise<string | null> => sosIdRef.current
      ? Promise.resolve(sosIdRef.current)
      : (pendingRef.current ?? Promise.resolve(null)),
    [],
  );

  /**
   * Close the moment out, with a rating or without one.
   *
   * Both buttons on the closing screen come through here, and that is the fix:
   * "Skip this" used to be `setStage('done')` and nothing else, so declining to
   * rate your distress meant the whole visit went unrecorded — a breathing
   * exercise worked through to the end left no trace anywhere, because the only
   * call that created the event lived inside Save.
   *
   * A skipped rating is a missing number, not a missing event. `distress_after`
   * stays null and the relief average passes over it, since that only reads
   * moments with both readings; the moment itself still counts, which is what
   * every screen means by "Help now".
   */
  const finish = useCallback(
    (distressAfter: number | null) => {
      // Creates the event if nothing else has yet. The screen moves on
      // regardless of whether either call lands — a lost row is a lost row, and
      // making someone wait on bookkeeping at this moment would be the app
      // putting itself first.
      void (async () => {
        // Closes what is there. Nothing is created here: arriving via "I'm okay
        // now" without having done a step means there is nothing to close, and
        // that is the correct record of what happened.
        const id = await startedEvent();
        if (!id) return;
        await api
          .closeSos(id, {
            distressAfter,
            distressBeforeRecalled: recalled,
            pathTaken: tool ? pathFor(tool) : null,
          })
          .catch(() => {});
      })();
      setStage('done');
    },
    [startedEvent, tool, recalled],
  );

  // Whatever went wrong, the crisis numbers still have to work — they are
  // compiled into the bundle for exactly this case. What changes is only the
  // explanation, and it has to be accurate: telling someone the server is
  // unreachable when their session simply expired sends them off checking
  // their wifi instead of tapping "sign in".
  if (failure) {
    const offline = failure.isOffline;
    const expired = failure.isUnauthorized;
    return (
      <div className="stack-lg stack">
        <header className="page-header">
          <h1>You're not on your own</h1>
          <p>
            {offline
              ? "This app can't reach its server right now — but these numbers don't need it."
              : expired
                ? "You were signed out, so this wasn't saved. The numbers below still work."
                : "Something went wrong saving this — the numbers below still work."}
          </p>
        </header>
        <OfflineCrisisCard />
        <div className="stack-sm stack">
          {expired && (
            <button className="btn btn-primary btn-block" onClick={() => window.location.reload()}>
              Sign in again
            </button>
          )}
          <button className="btn btn-secondary btn-block" onClick={() => navigate('/')}>
            Back to today
          </button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="app" style={{ display: 'grid', placeItems: 'center', minHeight: '60dvh' }}>
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  // --- Doing a tool -------------------------------------------------------
  if (stage === 'doing' && tool) {
    const isLast = step >= tool.steps.length - 1;
    return (
      <div className="stack-lg stack">
        <header className="page-header">
          <button
            className="btn btn-ghost"
            style={{ marginBottom: 'var(--space-3)', minHeight: 40, paddingLeft: 0 }}
            onClick={() => {
              setStage('choose');
              setStep(0);
            }}
          >
            <ArrowLeftIcon /> Something else
          </button>
          <h1>{tool.title}</h1>
          <p>
            Step {step + 1} of {tool.steps.length}
          </p>
        </header>

        <div className="card" style={{ padding: 'var(--space-6)' }}>
          <p className="reading">{tool.steps[step]}</p>
        </div>

        <div className="stack-sm stack">
          {!isLast ? (
            <button
              className="btn btn-primary btn-lg btn-block"
              onClick={() => {
                // Finishing a step is the first unambiguous thing that happens
                // here, and the moment is recorded from it rather than from the
                // rating at the end. Not from opening the screen, and not from
                // tapping a tool to see what is in it — both of those are how a
                // curious tap became "he asked for help three times today".
                void ensureEvent();
                setStep(step + 1);
              }}
            >
              Done — next
            </button>
          ) : (
            <button
              className="btn btn-primary btn-lg btn-block"
              onClick={() => {
                // Also here, for a tool that is one step long: there is no
                // "next" to press on those, so without this they would reach the
                // rating screen having recorded nothing.
                void ensureEvent();
                void api
                  .logToolSession({
                    toolKey: tool.key,
                    completedAt: new Date().toISOString(),
                    distressBefore: null,
                    distressAfter: null,
                  })
                  .catch(() => {});
                setStage('closing');
              }}
            >
              Done — finish
            </button>
          )}
          {step > 0 && (
            <button className="btn btn-ghost btn-block" onClick={() => setStep(step - 1)}>
              Back a step
            </button>
          )}
        </div>

        <CrisisToggle show={showCrisis} onToggle={() => setShowCrisis(!showCrisis)} session={session} />
      </div>
    );
  }

  // --- Closing out --------------------------------------------------------
  if (stage === 'closing') {
    return (
      <div className="stack-lg stack">
        <header className="page-header">
          <h1>How are you now?</h1>
          <p>Only if you want to. It's how you find out which of these actually work for you.</p>
        </header>

        <div className="card stack">
          <div className="field">
            <label htmlFor="after">Distress right now</label>
            <input
              id="after"
              className="slider"
              type="range"
              min={0}
              max={100}
              step={5}
              value={after}
              onChange={(e) => setAfter(Number(e.target.value))}
            />
            <div className="slider-scale">
              <span className="faint">Settled</span>
              <strong>{after}</strong>
              <span className="faint">Overwhelming</span>
            </div>
          </div>

          {/*
            And the one the app has never asked. Second, because "how are you
            now" is the question somebody arriving at this screen is ready for
            and this one asks them to look back at the thing they have just
            come out of.

            It is RECALL, and it is labelled as recall wherever it is drawn:
            people misremember how bad it was once it has passed, usually
            toward how they feel now, so it never shares an axis or an average
            with a reading taken at the time.
          */}
          <div className="field">
            <label htmlFor="recalled">And when you opened this?</label>
            <input
              id="recalled"
              className="slider"
              type="range"
              min={0}
              max={100}
              step={5}
              value={recalled ?? 50}
              onChange={(e) => setRecalled(Number(e.target.value))}
            />
            <div className="slider-scale">
              <span className="faint">Settled</span>
              <strong>{recalled === null ? 'Not said' : recalled}</strong>
              <span className="faint">Overwhelming</span>
            </div>
          </div>
        </div>

        <div className="stack-sm stack">
          <button
            className="btn btn-primary btn-lg btn-block"
            onClick={() => finish(after)}
          >
            Save
          </button>
          <button className="btn btn-ghost btn-block" onClick={() => finish(null)}>
            Skip this
          </button>
        </div>

        {/* Here too. Somebody who has just dragged their distress to the top of
            the scale is the likeliest reader of these numbers in the whole app,
            and this was the one stage that did not offer them. */}
        <CrisisToggle show={showCrisis} onToggle={() => setShowCrisis(!showCrisis)} session={session} />
      </div>
    );
  }

  // --- Done ---------------------------------------------------------------
  if (stage === 'done') {
    return (
      <div className="stack-lg stack">
        <header className="page-header" style={{ paddingTop: 'var(--space-8)' }}>
          <h1>You got through it</h1>
          <p>
            That's the whole thing. You noticed something was wrong and you did something about
            it, which is genuinely the hard part.
          </p>
        </header>
        <div className="stack-sm stack">
          <button className="btn btn-primary btn-block" onClick={() => navigate('/')}>
            Back to today
          </button>
          <button className="btn btn-secondary btn-block" onClick={() => navigate('/thought-record')}>
            Write down what happened
          </button>
        </div>
      </div>
    );
  }

  // --- Choose -------------------------------------------------------------
  return (
    <div className="stack-lg stack">
      <header className="page-header">
        <h1>Okay. Let's slow this down.</h1>
        <p>Pick whatever sounds least impossible right now.</p>
      </header>

      {/*
        Above the techniques, not below them.

        The emergency line was made unconditional and then left at the foot of a
        list of six cards: measured in a browser at 390x844, its top sits at
        y=881 on an 844-tall screen, and the last card ends 17px above the nav,
        so the page reads as finished. "Never behind the fold. One line, always"
        was true of the markup and false of the phone.
      */}
      <CrisisToggle show={showCrisis} onToggle={() => setShowCrisis(!showCrisis)} session={session} />

      <section className="card stack-sm stack how-bad" aria-label="How bad is it right now">
        <div className="row-between">
          <span>How bad is it, right now?</span>
          <span className="faint">{before === null ? 'skip if you like' : `${before} of 10`}</span>
        </div>
        <div className="scale10" role="group">
          {Array.from({ length: 11 }, (_, i) => (
            <button
              key={i}
              type="button"
              className="scale10-n"
              aria-pressed={before === i}
              onClick={() => {
                beforeRef.current = i;
                setBefore(i);
                void ensureEvent();
              }}
            >
              {i}
            </button>
          ))}
        </div>
      </section>

      <div className="stack-sm stack">
        {session.tools.map((t) => (
          <button
            key={t.key}
            className="option"
            onClick={() => {
              setTool(t);
              setStep(0);
              setStage('doing');
            }}
          >
            <strong>{t.title}</strong>
            <span>
              {t.blurb} · {t.durationMinutes} min
            </span>
          </button>
        ))}
      </div>

      <button className="btn btn-ghost btn-block" onClick={() => setStage('closing')}>
        I'm okay now
      </button>
    </div>
  );
}

/**
 * One resource, with the number as something you can press.
 *
 * The list used to be `<strong>{r.name}</strong> — {r.contact}`, plain text, on
 * the one screen in the app whose whole purpose is a person in acute distress.
 * Reaching help meant reading a sentence, holding a number in your head,
 * leaving the app and opening the dialer — four steps at exactly the moment
 * executive function is gone, and the app had the number the whole time.
 *
 * `contact` still leads, because it is the instruction in words and some of
 * these are text-first. The buttons are what it costs to act on it.
 */
/** Only the fields this renders — the session payload carries a narrower row
    than the seed file does, and neither needs to know about the other. */
type Contactable = Pick<CrisisResource, 'name' | 'contact' | 'description' | 'available'>
  & Partial<Pick<CrisisResource, 'dial' | 'sms'>>;

function CrisisContact({ r }: { r: Contactable }): JSX.Element {
  return (
    <div className="crisis-contact">
      <strong>{r.name}</strong> — {r.contact}
      {(r.dial || r.sms) && (
        <div className="crisis-actions">
          {r.dial && (
            // The number is already on the line above, in the form the
            // provider publishes it. Repeating it here as raw digits —
            // "Call 18006624357" — reads worse, not clearer.
            <a className="btn btn-crisis" href={`tel:${r.dial}`}>
              Call
            </a>
          )}
          {r.sms && (
            <a
              className="btn btn-crisis"
              href={`sms:${r.sms.number}${r.sms.body ? `?&body=${encodeURIComponent(r.sms.body)}` : ''}`}
            >
              {r.sms.body ? `Text ${r.sms.body}` : 'Text'} {r.sms.number}
            </a>
          )}
        </div>
      )}
      <span className="faint">
        {r.description} · {r.available}
      </span>
    </div>
  );
}

function CrisisToggle({
  show,
  onToggle,
  session,
}: {
  show: boolean;
  onToggle: () => void;
  session: SosStart;
}): JSX.Element {
  return (
    <div className="stack-sm stack">
      {/* Never behind the fold. This is the 911/999/000/112 line, and it used
          to appear only after pressing a button labelled with a feeling — so a
          person who did not identify with "I need to talk to a person" never
          saw an emergency number at all. One line, always. */}
      <p className="crisis-emergency">{session.crisis.emergencyNote}</p>
      {/*
        The number, as something to press.
        
        Every named helpline below this has had a Call button since somebody
        wrote, about this exact screen: "reaching help meant reading a sentence,
        memorising a number, leaving the app and opening the dialer — four
        steps at the moment executive function is gone." That argument was made
        about the helplines and never reached the line above them, which is the
        one for immediate danger. It stayed as prose, with all four steps.

        Only where the region says which number it is. A button that dials the
        wrong emergency number is worse than a sentence naming four, so INTL
        keeps the sentence and gets no button.
      */}
      {EMERGENCY_DIAL[session.crisis.region] && (
        <a className="btn btn-crisis btn-block" href={`tel:${EMERGENCY_DIAL[session.crisis.region]}`}>
          Call {EMERGENCY_DIAL[session.crisis.region]}
        </a>
      )}
      <button className="btn btn-ghost btn-block" onClick={onToggle} aria-expanded={show}>
        {/* Names what is behind it rather than describing a mood. */}
        {show ? 'Hide crisis lines' : 'Crisis lines and helplines'}
      </button>
      {show && (
        <div className="banner banner-crisis stack-sm stack">
          {session.crisis.resources.map((r) => (
            <CrisisContact key={r.name} r={r} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Baked into the bundle so it survives the server being down, the network
 * being gone, or the API failing. This is the one thing in the app that must
 * never depend on a request succeeding.
 */
function OfflineCrisisCard(): JSX.Element {
  return (
    <div className="banner banner-crisis stack-sm stack">
      {/* The shared constant, not a second hand-typed copy. This one had lost
          "or you have taken something" — so the version shown when the network
          is down, which is not a less serious moment, left out the overdose
          case. Baking it in still works: it is a compile-time import, so the
          card survives the server being unreachable exactly as before. */}
      <strong>{EMERGENCY_NOTE}</strong>
      {/* Written out rather than mapped over the seeded list on purpose: this
          card exists for the case where nothing was fetched. */}
      <CrisisContact
        r={{
          name: '988 Suicide & Crisis Lifeline (US)', contact: 'Call or text 988',
          dial: '988', sms: { number: '988', body: '' },
          description: 'Free, confidential support for anyone in distress.', available: '24/7',
        }}
      />
      <CrisisContact
        r={{
          name: 'Crisis Text Line (US)', contact: 'Text HOME to 741741',
          sms: { number: '741741', body: 'HOME' },
          description: 'Text-based support with a trained crisis counsellor.', available: '24/7',
        }}
      />
      <CrisisContact
        r={{
          name: 'Samaritans (UK)', contact: 'Call 116 123', dial: '116123',
          description: 'Free from any phone, day or night.', available: '24/7',
        }}
      />
      <div>
        <strong>Find a Helpline</strong> — findahelpline.com, for anywhere else
      </div>
    </div>
  );
}

function pathFor(tool: CbtTool): string {
  if (tool.category === 'grounding') return 'ground';
  if (tool.category === 'cognitive') return 'thought_record';
  if (tool.category === 'emotion') return 'just_wait';
  return 'ground';
}
